/**
 * HOW WIDE A RUN IS, WITH NO FONT ENGINE IN THE ROOM.
 *
 * `labelFit` takes its measurement as a parameter precisely so the rule can
 * be shared by callers who measure differently. The app hands it Skia's own
 * glyph advances off the loaded face (`app/src/textMeasure.ts`).
 * `render.ts` cannot: it is a pure function over a document, called from a
 * browser, from a Worker, and from a test, with no typeface anywhere. So it
 * hands it THIS — the shipped face's own `hmtx` advances, generated into
 * `baloo2Metrics.ts` from `workers/assets/Baloo2-400Regular.ttf` and
 * re-derived by a guard on every run.
 *
 * WHAT THIS IS EXACT ABOUT, AND WHAT IT IS ONLY CLOSE ABOUT — measured,
 * 2026-09-03, because a fit built on a width model is only as honest as the
 * model's error bar:
 *
 *  - Against the SAME face with kerning off it is exact. Chrome, this very
 *    `.ttf` loaded via `@font-face`, `fontKerning: "none"`: the sums here
 *    reproduce `measureText` to 3e-7 em on AV, To, Ta, Yo, LT, AWAY and
 *    "Zen of Venn" — float noise, not error.
 *  - Against the same face AS ACTUALLY SHAPED (GPOS kerning on, which is
 *    every browser's and resvg's default) it is within half a percent on
 *    real labels: "lovers of the world" renders 0.38% narrower than this
 *    says, "poetry, presents, dreams, dates" 0.11%, "things that make me
 *    happy" 0.20%, while "Cats" renders 0.37% WIDER — GPOS moves both
 *    ways. A bare kern pair is the worst case ("To", 6.4% narrower), and a
 *    label is not a kern pair.
 *  - Against a FALLBACK face it is a good estimate and nothing more, and
 *    this case is live today: the site names 'Baloo 2' in its font stack
 *    but never loads it, so a shared venn opened in a browser is painted
 *    in system-ui. Measured in Chrome on macOS at 100px, system-ui (San
 *    Francisco) runs 2.5-6.5% NARROWER than Baloo on ordinary sentences
 *    ("lovers of the world" 0.936 of it, "building venns together when
 *    apart" 0.960, "things that make me happy" 0.972) and Helvetica
 *    2-5% WIDER ("people who inspire me" 1.040, "poetry, presents,
 *    dreams, dates" 1.054). Both are small next to the failure this
 *    measurement exists to stop — a label three to five times wider than
 *    its region — and neither is a reason to fit against a face nobody
 *    named. (Serving Baloo from the site would retire the whole
 *    paragraph; that is issue 304, not this file's business.)
 *
 * THE INK, SEPARATELY, because the leading depends on it. Baloo 2's hhea
 * line box is 1.602 em (ascender 1.078 + descender 0.524), which is a
 * Devanagari-sized box and nothing to do with what Latin reaches. Measured
 * through `measureText`'s `actualBoundingBox*` on the same loaded face,
 * over a corpus with ascenders, descenders and caps: ink spans at most
 * 0.670 em above the baseline and 0.202 em below it — 0.872 em all told.
 * That is why `render.ts` sets its lines 1.2 em apart and not 1.762: at
 * 1.2 there is still 0.33 em of clear air between one line's descenders
 * and the next line's ascenders, and a block of three lines stays a block
 * rather than a tower.
 */
import { BALOO2_ADVANCES, BALOO2_UNITS_PER_EM } from './baloo2Metrics.ts';

/**
 * What a code point Baloo has never heard of costs, in em.
 *
 * ONE EM, WHICH IS DELIBERATELY MORE THAN ANY MEASURER WOULD REPORT for
 * the resvg case: with only Baloo in its font database, resvg draws
 * `.notdef` at 0.645 em (`BALOO2_NOTDEF_ADVANCE`). A browser does the
 * opposite — it substitutes a fallback face per character, and an emoji
 * there is roughly a full em or a little more. Charging an em serves the
 * browser case honestly and the rasterizer case conservatively, and the
 * conservative direction is the one where a label comes out slightly
 * small rather than slightly over its region.
 *
 * This is the fitting half of the emoji story, not the whole of it: what
 * an emoji should LOOK like in a shared venn — whether the picture
 * carries an emoji font at all — is issue 34.
 */
export const UNKNOWN_ADVANCE_EM = 1;

/** Built once, on first use. 856 entries; a Map lookup per character is
 *  nothing next to the k²/2 candidate runs `typesetsFor` measures, and
 *  building it eagerly would cost every importer of `@zov/core` the work
 *  whether or not they ever draw a label. */
let advances: Map<number, number> | null = null;

function advanceTable(): Map<number, number> {
  if (advances === null) {
    advances = new Map();
    for (let i = 0; i + 1 < BALOO2_ADVANCES.length; i += 2) {
      advances.set(BALOO2_ADVANCES[i]!, BALOO2_ADVANCES[i + 1]!);
    }
  }
  return advances;
}

/**
 * A run's advance width in EM, summed over CODE POINTS.
 *
 * Code points, not code units, and the distinction is not academic here:
 * `for…of` over a string yields astral characters whole, so an emoji costs
 * one `UNKNOWN_ADVANCE_EM` rather than two. Iterating code units would
 * charge every emoji double and shrink the label to fit a width nothing
 * would ever draw.
 *
 * CODE POINTS, NOT GRAPHEME CLUSTERS, and here the count runs the OTHER
 * way: a ZWJ family emoji or a VS16-qualified glyph is several code points
 * that a browser draws as one cluster roughly a single em wide, so this
 * charges it 3-7 em where the paint spends about 1. That over-estimate is
 * the conservative direction — it can only shrink a label, never overflow
 * one — so it is left as a known slack rather than fixed here; the honest
 * width of an emoji is the emoji round's to settle (issue 34), alongside
 * what one should look like at all.
 */
export function balooEmWidth(text: string): number {
  const table = advanceTable();
  let total = 0;
  for (const character of text) {
    const advance = table.get(character.codePointAt(0)!);
    total +=
      advance === undefined
        ? UNKNOWN_ADVANCE_EM
        : advance / BALOO2_UNITS_PER_EM;
  }
  return total;
}
